import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { api } from '../../utils/api';
import './Auth.css';
import logo from '../../assets/logo.png';

function PasswordSetup() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const token = searchParams.get('token');

  const criteria = useMemo(() => ({
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    passwordsMatch: password.length > 0 && password === confirmPassword,
  }), [password, confirmPassword]);

  const allCriteriaMet = Object.values(criteria).every(Boolean);

  useEffect(() => {
    if (!token) {
      setError('Invalid setup link');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allCriteriaMet) return;

    setError('');
    setLoading(true);

    try {
      await api.setupPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  const CriteriaItem = ({ met, label }) => (
    <div className={`criteria-item ${met ? 'met' : ''}`}>
      <div className={`criteria-icon ${met ? 'met' : ''}`}>
        {met ? <Check size={12} /> : <X size={12} />}
      </div>
      <span>{label}</span>
    </div>
  );

  if (!token) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <div className="auth-header">
            <div className="auth-logo-div">
              <img src={logo} alt="Logo" />
            </div>
            <p>Invalid setup link</p>
          </div>
          <div className="error-message">
            This password setup link is invalid or has expired. Please contact your administrator.
          </div>
          <button onClick={() => navigate('/login')} className="btn-login">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <div className="auth-header">
            <div className="auth-logo-div">
              <img src={logo} alt="Logo" />
            </div>
            <p>Password set successfully</p>
          </div>
          <div className="info-message" style={{ textAlign: 'center' }}>
            Your password has been set. Redirecting to login...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="auth-header">
          <div className="auth-logo-div">
            <img src={logo} alt="Logo" />
          </div>
          <p>Create a secure password for your account</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              disabled={loading}
            />
          </div>

          <div className="criteria-box">
            <CriteriaItem met={criteria.minLength} label="At least 8 characters" />
            <CriteriaItem met={criteria.hasUppercase} label="One uppercase letter" />
            <CriteriaItem met={criteria.hasNumber} label="One number" />
            <CriteriaItem met={criteria.passwordsMatch} label="Passwords match" />
          </div>

          <button
            type="submit"
            className="btn-login"
            disabled={!allCriteriaMet || loading}
          >
            {loading ? 'Setting password...' : 'Set Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default PasswordSetup;
